import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '@config/environments/environment';

/**
 * Logging levels
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SECURITY = 4,
}

/**
 * Log entry structure
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: Record<string, unknown>;
  stackTrace?: string;
  userInfo?: {
    id?: number;
    email?: string;
    roles?: string[];
  };
}

/**
 * LoggerService - Centralized logging for the application
 *
 * Features:
 * - Multiple log levels (DEBUG, INFO, WARN, ERROR, SECURITY)
 * - Environment-aware (verbose in dev, minimal in prod)
 * - Security event tracking
 * - User context inclusion
 * - Error stack trace capture
 * - Optional backend logging
 *
 * Usage:
 * ```typescript
 * constructor(private logger: LoggerService) {}
 *
 * // Error logging
 * this.logger.error('Failed to load user', { userId: 123 });
 *
 * // Security event
 * this.logger.security('Unauthorized access attempt', { route: '/admin' });
 *
 * // Info
 * this.logger.info('User logged in', { email: 'user@example.com' });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private readonly isDevelopment = !environment.production;
  private readonly minLogLevel = this.isDevelopment
    ? LogLevel.DEBUG
    : LogLevel.WARN;

  private readonly logEndpoint = `${environment.api}/security/log`;

  private readonly http: HttpClient;

  /**
   * Client construit sur HttpBackend, donc hors chaîne d'intercepteurs.
   *
   * ApiHttpInterceptor journalise un événement de sécurité sur 401 et 403, et
   * une erreur sur tout autre échec. Comme /security/log passait par lui, un
   * seul POST de journal en échec se réamorçait indéfiniment : l'échec était
   * journalisé, ce qui produisait un POST, qui échouait, et ainsi de suite. Le
   * limiteur de débit côté API (60/heure) transformait la boucle en 429
   * permanents sur l'endpoint même qui sert à détecter les attaques.
   */
  constructor(handler: HttpBackend) {
    this.http = new HttpClient(handler);
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, context);
  }

  /**
   * Log informational messages
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, context);
  }

  /**
   * Log warnings
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, context);
  }

  /**
   * Log errors with stack trace
   */
  error(
    message: string,
    error?: Error | unknown,
    context?: Record<string, unknown>,
  ): void {
    const stackTrace = error instanceof Error ? error.stack : undefined;
    const errorContext = {
      ...context,
      errorMessage: error instanceof Error ? error.message : String(error),
    };

    this.log(LogLevel.ERROR, message, errorContext, stackTrace);
  }

  /**
   * Log security events (always logged, even in production)
   *
   * Examples:
   * - Login attempts
   * - Logout
   * - Access denied (403)
   * - Permission changes
   * - Authentication failures
   */
  security(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.SECURITY, message, context);
  }

  /**
   * Internal logging method
   */
  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    stackTrace?: string,
  ): void {
    // Check if this log level should be processed
    if (level < this.minLogLevel && level !== LogLevel.SECURITY) {
      return;
    }

    const logEntry: LogEntry = {
      level,
      message: this.sanitizeMessage(message),
      timestamp: new Date(),
      context: this.sanitizeContext(context),
      stackTrace,
      userInfo: this.getUserContext(),
    };

    // Console output (development)
    if (this.isDevelopment) {
      this.consoleLog(logEntry);
    }

    if (
      level === LogLevel.ERROR ||
      level === LogLevel.SECURITY ||
      (!this.isDevelopment && level >= LogLevel.WARN)
    ) {
      this.sendToBackend(logEntry);
    }
  }

  /**
   * Output to console with appropriate styling
   */
  private consoleLog(entry: LogEntry): void {
    const timestamp = entry.timestamp.toISOString();
    const prefix = `[${LogLevel[entry.level]}] ${timestamp}`;

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.INFO:
        console.info(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.WARN:
        console.warn(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.ERROR:
        console.error(prefix, entry.message, entry.context || '');
        if (entry.stackTrace) {
          console.error('Stack trace:', entry.stackTrace);
        }
        break;
      case LogLevel.SECURITY:
        console.warn(`🔒 ${prefix}`, entry.message, entry.context || '');
        break;
    }
  }

  /**
   * Send log to backend (async, non-blocking).
   * Fire-and-forget: errors are silently ignored so that logging
   * can never break the user's flow.
   */
  private sendToBackend(entry: LogEntry): void {
    this.http
      .post(
        this.logEndpoint,
        {
          type: LogLevel[entry.level],
          timestamp: entry.timestamp.getTime(),
          statusCode: undefined,
          userAgent: navigator.userAgent,
          metadata: { message: entry.message, ...entry.context },
        },
        // Posé explicitement : sans intercepteur, rien ne l'ajoute plus.
        { withCredentials: true },
      )
      .subscribe({
        error: () => {
          // Silently fail - logging should never break user experience
        },
      });
  }

  /**
   * Get current user context from auth state
   */
  private getUserContext(): LogEntry['userInfo'] {
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        return {
          id: userInfo.id,
          email: userInfo.email,
          roles: userInfo.roles,
        };
      }
    } catch {
      // Silently fail if can't get user info
    }
    return undefined;
  }

  /**
   * Sanitize message to remove sensitive data
   */
  private sanitizeMessage(message: string): string {
    // Truncate very long messages
    if (message.length > 1000) {
      return message.substring(0, 1000) + '... (truncated)';
    }
    return message;
  }

  /**
   * Sanitize context to remove sensitive data
   */
  private sanitizeContext(
    context?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    if (!context) return undefined;

    const sanitized: Record<string, unknown> = {};
    const sensitiveKeys = [
      'password',
      'token',
      'jwt',
      'secret',
      'apiKey',
      'creditCard',
    ];

    for (const [key, value] of Object.entries(context)) {
      const lowerKey = key.toLowerCase();

      // Check if key contains sensitive term
      if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }
}
